import React from 'react';
import PropTypes from 'prop-types';
import { Loading } from '@meronex/components';
import IChartIcon from '@meronex/icons/fi/FiBarChart2';

import { App } from '@meronex/app';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

import SitesAvatar from '../../../common/SitesAvatar';
import Quantity from '../../../common/Quantity';
import AnalyticsDialog from '../storeItems/analysis/AnalyticsDialog';

import { storesMng } from '../../api/storesMng';

const utils = App.utils;

function Stats(props) {
  const { itemsMap, fetching, fetchError } = props;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isDark = theme.palette.type === 'dark';

  const [openAnalyticsDialog, setOpenAnalyticsDialog] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(false);

  if (fetchError) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 500,
          color: isDark ? '#f87171' : '#dc2626',
          marginBottom: '8px',
        }}>
          Failed to load analytics
        </div>
        <div style={{ fontSize: '13px', color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
          {fetchError}
        </div>
        <div style={{
          fontSize: '12px',
          color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8',
          marginTop: '12px',
        }}>
          Try a shorter time range for large stores.
        </div>
      </div>
    );
  }

  if (!itemsMap || fetching) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          margin: '0 auto 12px',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontSize: '13px', color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
          Loading analytics...
        </div>
      </div>
    );
  }

  const round = (n) => Math.round(n * 100) / 100;

  const getTotals = (item) => {
    const totalIn = round(item.in + item.transferIn);
    const totalOut = round(item.out + item.transferOut);
    const net = round(totalIn + totalOut);
    return { in: totalIn, out: totalOut, net };
  };

  const onItemClick = async (item) => {
    try {
      const itemDoc = await storesMng.getStoreItem(item.storeItem._id);
      setSelectedItem(itemDoc);
      setOpenAnalyticsDialog(true);
    } catch (e) {
      console.error('Failed to load store item:', e);
    }
  };

  // Build stats array and compute summary
  const items = [];
  let summaryIn = 0;
  let summaryOut = 0;

  for (let [key, value] of itemsMap) {
    const totals = getTotals(value);
    summaryIn += totals.in;
    summaryOut += Math.abs(totals.out);
    items.push({ ...value, totals });
  }

  summaryIn = round(summaryIn);
  summaryOut = round(summaryOut);
  const summaryNet = round(summaryIn - summaryOut);

  const renderSummary = () => {
    const cards = [
      { label: 'Active Items', value: items.length.toLocaleString(), color: isDark ? '#f1f5f9' : '#0f172a', bg: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc' },
      { label: 'Total In', value: `+${summaryIn.toLocaleString()}`, color: '#16a34a', bg: isDark ? 'rgba(22, 163, 74, 0.08)' : '#f0fdf4' },
      { label: 'Total Out', value: `-${summaryOut.toLocaleString()}`, color: '#dc2626', bg: isDark ? 'rgba(220, 38, 38, 0.08)' : '#fef2f2' },
      { label: 'Net', value: `${summaryNet >= 0 ? '+' : ''}${summaryNet.toLocaleString()}`, color: summaryNet >= 0 ? '#16a34a' : '#dc2626', bg: isDark ? 'rgba(99, 102, 241, 0.08)' : '#eef2ff' },
    ];

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cards.length}, 1fr)`,
        gap: '10px',
        padding: '16px 20px',
      }}>
        {cards.map((card, i) => (
          <div key={i} style={{
            textAlign: 'center',
            padding: isDesktop ? '14px 8px' : '16px 8px',
            borderRadius: '12px',
            backgroundColor: card.bg,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8',
              marginBottom: '6px',
            }}>
              {card.label}
            </div>
            <div style={{
              fontSize: isDesktop ? '22px' : '24px',
              fontWeight: 700,
              color: card.color,
            }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderItem = (item, index) => {
    const { totals } = item;
    const type = item.storeItem.type;
    const avatarSize = isDesktop ? 34 : 48;

    return (
      <div
        key={type._id}
        onClick={() => onItemClick(item)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: isDesktop ? '8px 20px' : '10px 16px',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease',
          borderBottom: index < items.length - 1
            ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`
            : 'none',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <div style={{ flexShrink: 0, marginRight: '12px' }}>
          <SitesAvatar
            image={type.image}
            name={type.name}
            size={avatarSize}
            round={8}
          />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: '13px',
            fontWeight: 500,
            color: isDark ? '#f1f5f9' : '#0f172a',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {type.name}
        </div>
        {isDesktop && (
          <>
            <div
              style={{
                width: '80px',
                textAlign: 'right',
                fontSize: '13px',
                fontWeight: 500,
                color: '#16a34a',
                marginRight: '10px',
              }}>
              {totals.in > 0 ? `+${totals.in.toLocaleString()}` : ''}
            </div>
            <div
              style={{
                width: '80px',
                textAlign: 'right',
                fontSize: '13px',
                fontWeight: 500,
                color: '#dc2626',
                marginRight: '10px',
              }}>
              {totals.out < 0 ? totals.out.toLocaleString() : ''}
            </div>
          </>
        )}
        <div
          style={{
            minWidth: isDesktop ? '80px' : '60px',
            textAlign: 'right',
            marginRight: isDesktop ? '10px' : '5px',
          }}>
          <Quantity
            showPositiveSign
            enableChangeDirAnimation={false}
            unitLabel={type.unit}
            inlineUnit
            quantitySize={isDesktop ? 14 : 16}
            textAlign={'right'}
            unitLabelStyle={{ textAlign: 'right' }}
            quantity={totals.net}
          />
        </div>
      </div>
    );
  };

  // Column headers (desktop only)
  const renderHeader = () => {
    if (!isDesktop) return null;

    const headerStyle = {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8',
    };

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 20px',
          backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#fafbfc',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
        }}>
        <div style={{ width: `${(isDesktop ? 34 : 48) + 12}px` }} />
        <div style={{ ...headerStyle, flex: 1 }}>Item</div>
        <div style={{ ...headerStyle, width: '80px', textAlign: 'right', marginRight: '10px' }}>
          In
        </div>
        <div style={{ ...headerStyle, width: '80px', textAlign: 'right', marginRight: '10px' }}>
          Out
        </div>
        <div style={{ ...headerStyle, width: '80px', textAlign: 'right', marginRight: '10px' }}>
          Net
        </div>
      </div>
    );
  };

  if (items.length === 0) {
    return (
      <div style={{ padding: '48px 20px', textAlign: 'center' }}>
        <div style={{
          fontSize: '36px',
          marginBottom: '12px',
          opacity: 0.3,
        }}>
          <IChartIcon size={36} />
        </div>
        <div style={{
          fontSize: '15px',
          fontWeight: 600,
          color: isDark ? '#e2e8f0' : '#0f172a',
          marginBottom: '6px',
        }}>
          No transactions found
        </div>
        <div style={{
          fontSize: '13px',
          color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8',
        }}>
          No activity for this period. Try a longer time range.
        </div>
      </div>
    );
  }

  return (
    <div>
      {renderSummary()}
      {renderHeader()}
      {items.map((item, i) => renderItem(item, i))}

      {selectedItem && (
        <AnalyticsDialog
          item={selectedItem}
          open={openAnalyticsDialog}
          onClose={() => {
            setOpenAnalyticsDialog(false);
          }}
        />
      )}
    </div>
  );
}
Stats.propTypes = {};
Stats.defaultProps = {};
export default Stats;
